import { useId, useMemo, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ActionSetting, ResolvedSetting, SettingsMode } from './types';
import { InfoHint } from './settings/InfoHint';
import { controlFor } from './settings/registry';
import { canChange, groupSettings, lockNote, resolveSettings } from './settings/resolve';
import { validateSettings } from './settings/validate';
import { divide, hint as hintInk, ink, lead as leadClass, line, muted, page, small } from './tokens';

/**
 * How the form is laid out. `stacked`: each label over its control, for the
 * viewer's Run dialog. `rows`: each group on its own surface, one row per
 * setting with the words first and its controls in lines that share one lead
 * word's width, so their sliders and fields line up; for a workspace's page.
 */
export type SettingsLayout = 'stacked' | 'rows';

export interface SettingsFormProps {
  /** The action's settings as the server resolves them (`action.settings`). */
  settings: Record<string, ActionSetting> | null | undefined;
  /**
   * 'workspace': a workspace's values; 'run': this run's. Every setting is
   * shown; those the mode cannot change are drawn locked, saying who set them.
   */
  mode: SettingsMode;
  /** The stored values, by setting (a ratio stays 0..1). */
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  /** Read-only: the values shown, nothing changeable. */
  disabled?: boolean;
  layout?: SettingsLayout;
  /** Something at the right of a setting's title line: a workspace's page puts the "Allow users to customize" switch there. */
  aside?: (setting: ResolvedSetting) => ReactNode;
  /** The word that leads the control's line in the `rows` layout ("Default"). */
  lead?: (setting: ResolvedSetting) => ReactNode;
  /** More lines under a setting's control: a workspace's page puts the band runs may use there. */
  extra?: (setting: ResolvedSetting) => ReactNode;
}

/**
 * One form for every action's settings (plan, "UI: modular"), drawn from the
 * resolved `ui` hints through the control registry: in groups (`ui.group`, in
 * the order their first setting comes), each in `ui.order`, the advanced ones
 * behind "More settings". Values are checked as the server will check them,
 * and each problem is said under its field; the caller checks the same with
 * validateSettings before it sends.
 */
export function SettingsForm({ settings, mode, values, onChange, disabled = false, layout = 'stacked', aside, lead, extra }: SettingsFormProps) {
  const prefix = useId();
  const all = useMemo(() => resolveSettings(settings), [settings]);
  const errors = useMemo(() => validateSettings(settings, values, mode), [settings, values, mode]);
  const hasAdvanced = all.some((s) => s.ui.advanced);
  const [more, setMore] = useState(false);
  // An advanced setting with a problem is shown whatever the toggle says: a
  // refusal nobody can see is a dead end.
  const shown = all.filter((s) => !s.ui.advanced || more || !!errors[s.key]);
  const groups = groupSettings(all, shown);
  const rows = layout === 'rows';

  if (all.length === 0) {
    return <p className={cn('text-sm', muted)}>This action has no settings.</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      {groups.map((g) => (
        <fieldset key={g.title} className="flex min-w-0 flex-col">
          {rows || groups.length > 1 || g.title !== 'Settings' ? (
            <legend className={cn('mb-2 text-xs font-semibold uppercase tracking-wide', hintInk)}>{g.title}</legend>
          ) : null}
          <div className={rows ? cn('divide-y overflow-hidden rounded-lg border', line, divide, page) : 'flex flex-col gap-4'}>
            {g.settings.map((s) => (
              <SettingField
                key={s.key}
                id={`${prefix}-${s.key}`}
                setting={s}
                value={values[s.key]}
                error={errors[s.key]}
                disabled={disabled}
                locked={!canChange(s, mode)}
                note={canChange(s, mode) ? null : lockNote(s, mode)}
                rows={rows}
                aside={aside?.(s)}
                lead={rows ? lead?.(s) : undefined}
                extra={extra?.(s)}
                onChange={(v) => onChange({ ...values, [s.key]: v })}
              />
            ))}
          </div>
        </fieldset>
      ))}
      {hasAdvanced ? (
        <button
          type="button"
          aria-expanded={more}
          onClick={() => setMore(!more)}
          className={cn(
            'inline-flex items-center gap-1 self-start rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            muted,
            'hover:text-[color:var(--tr-ink,var(--foreground))]',
          )}
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', more && 'rotate-180')} />
          {more ? 'Fewer settings' : 'More settings'}
        </button>
      ) : null}
    </div>
  );
}

function SettingField({
  id,
  setting,
  value,
  error,
  disabled,
  locked,
  note,
  rows,
  aside,
  lead,
  extra,
  onChange,
}: {
  id: string;
  setting: ResolvedSetting;
  value: unknown;
  error?: string;
  disabled: boolean;
  /** This form cannot change it. */
  locked: boolean;
  /** Who set it, when that is said. */
  note: string | null;
  rows: boolean;
  aside?: ReactNode;
  lead?: ReactNode;
  extra?: ReactNode;
  onChange: (v: unknown) => void;
}) {
  const Control = controlFor(setting);
  const errorId = error ? `${id}-error` : undefined;
  // The words under the label are the setting's description; the info icon
  // keeps the longer hint and the link, and is not shown for a hint that only
  // repeats the description.
  const description = setting.ui.description ?? null;
  const longHint = setting.ui.hint && setting.ui.hint !== description ? setting.ui.hint : null;
  const descriptionId = description ? `${id}-description` : undefined;
  // What the control is described by: its words, and its problem when it has one.
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cn('flex min-w-0 flex-col gap-2', rows && 'px-5 py-4')} data-setting={setting.key} data-locked={locked ? 'true' : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <label htmlFor={id} className={cn('text-sm font-medium', ink)}>
              {setting.ui.title}
            </label>
            <InfoHint label={setting.ui.title} hint={longHint} link={setting.ui.link} />
            {note ? <span className={cn('text-xs', hintInk)}>· {note}</span> : null}
          </div>
          {description ? <p id={descriptionId} className={cn(small, muted)}>{description}</p> : null}
        </div>
        {aside}
      </div>
      <div className={cn('flex items-center gap-3', rows && 'max-w-2xl')}>
        {lead !== undefined ? <span className={leadClass}>{lead}</span> : null}
        <div className="min-w-0 flex-1">
          <Control id={id} setting={setting} value={value} onChange={onChange} disabled={disabled || locked} invalid={!!error} describedBy={describedBy} />
        </div>
      </div>
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
      {extra}
    </div>
  );
}
